import React from 'react'
import { observer } from 'mobx-react-lite'
import { useStores } from '@trivie/core'
import {
  Image,
  ImageStyle,
  TouchableOpacity,
  View,
  ViewStyle,
  Platform,
  TextStyle,
  Dimensions,
} from 'react-native'
import { Text } from '../text'
import { color } from '../../theme'
import { RecallWheel } from '../recall-wheel'
import { SvgIcon } from '../svg-icon'
import { AssignedTopic } from '@trivie/core/src/models/AssignedTopic'
import { Instance } from 'mobx-state-tree'
import {
  iconBronzeMedallion,
  iconCertNotEarned,
  iconCertNotStarted,
  iconGoldMedallion,
  iconLibraryLock,
  iconLibraryPlay,
  iconLibraryPlay_White,
  iconSilverMedallion,
  iconLibraryLock_Grey,
  iconPath,
} from '../../assets/icons'
import { UserPath } from '@trivie/core/src/models/workflow/Path'

import defaultTopicImage from '../../assets/images/backdrops/default-backdrop.png'
import { CountdownTimer } from '../countdown-timer'

interface TopicCardProps {
  topic?: Instance<typeof AssignedTopic>
  path?: Instance<typeof UserPath>
  onPress: (item: any) => void
  locked?: boolean
  image?: any
  style?: ViewStyle
  hideCountdown?: boolean
  hideBg?: boolean
}

export const TopicCard = observer((props: TopicCardProps) => {
  const { hideBg, style, topic, image, locked, path, onPress, hideCountdown, ...rest } = props
  const { userStore } = useStores()
  const isSubscriptionWorkspace = userStore.isSubscriptionWorkspace

  const completed = topic
    ? topic?.topic?.pct_complete === 1
    : path
      ? path.nodes_completed === path.node_count
      : false

  const started = topic
    ? topic?.topic?.pct_complete && topic?.topic?.pct_complete > 0 && !completed
    : path
      ? path.nodes_completed > 0 && !completed
      : false

  const certEnabled = path ? false : topic?.certification_enabled
  const certLevel = path ? null : topic?.user_cert_level

  const progressValue = topic
    ? topic.topic.pct_complete
    : path
      ? path.nodes_completed / path.node_count
      : 0

  const playIcon = path
    ? iconPath
    : !!topic?.topic?.pack_color && !hideBg
      ? iconLibraryPlay_White
      : iconLibraryPlay
  const recallWheel_circleColour = path
    ? color.blue
    : !!topic?.topic?.pack_color && !hideBg
      ? color.white
      : 'rgba(27, 89, 248, 1)'
  const recallWheel_strokeColour = path
    ? 'rgba(27, 89, 248, 0.1)'
    : !!topic?.topic?.pack_color && !hideBg
      ? '#ffffff20'
      : 'rgba(27, 89, 248, 0.2)'
  const recallWheel_iconColour = path
    ? color.blue
    : !!topic?.topic?.pack_color && !hideBg
      ? color.white
      : 'rgba(27, 89, 248, 0.8)'

  const recallWheelComp_circleColour =
    !!topic?.topic?.pack_color && !hideBg ? color.white : 'rgba(129, 52, 254, 1)'
  const recallWheelComp_strokeColour =
    !!topic?.topic?.pack_color && !hideBg ? '#ffffff20' : 'rgba(129, 52, 254, 0.1)'
  const recallWheelComp_iconColour =
    !!topic?.topic?.pack_color && !hideBg ? color.white : 'rgba(129, 52, 254, 0.8)'

  const lockIcon = iconLibraryLock_Grey

  const handlePress = () => {
    if (topic) {
      onPress(topic)
    } else if (path) {
      onPress(path)
    }
  }

  const photoUrl = topic?.topic?.photo || path?.image_url

  let imageSource = photoUrl
    ? {
        uri: photoUrl,
        cache: 'force-cache',
      }
    : image

  if (Platform.OS === 'ios' && !photoUrl) {
    imageSource = defaultTopicImage
  } else if (Platform.OS === 'ios' && photoUrl) {
    imageSource = {
      uri: photoUrl,
      cache: 'force-cache',
    }
  } else if (Platform.OS === 'web' && !photoUrl) {
    imageSource = defaultTopicImage
  }

  const subTitleTextColor = !!topic?.topic?.pack_color && !hideBg ? color.white : '#9098A3'

  const renderSubtitle = () => {
    if (path) {
      return (
        <View style={{ ...COLUMN, justifyContent: 'flex-end' }}>
          <Text
            variant="body2Medium"
            style={{
              color: subTitleTextColor,
            }}
            tx={
              path.nodes_completed < path.node_count && path.nodes_completed > 0
                ? 'Quiz.inProgress'
                : path.nodes_completed >= path.node_count
                  ? 'Common.complete'
                  : 'Topic.notStarted'
            }
          />
          <Text
            variant="subtitle"
            style={{ color: subTitleTextColor }}
            tx={
              (path.nodes_completed > 0 && path.nodes_completed !== path.node_count) ||
              path.nodes_completed >= path.node_count
                ? 'Library.pathSteps'
                : 'Library.pathStepsNotStarted'
            }
            txOptions={
              (path.nodes_completed > 0 && path.nodes_completed !== path.node_count) ||
              path.nodes_completed === path.node_count
                ? {
                    count: path.node_count,
                    total:
                      path.nodes_completed > path.node_count
                        ? path.node_count
                        : path.nodes_completed,
                  }
                : { count: path.node_count }
            }
          />
        </View>
      )
    } else if (locked) {
      return (
        <View style={COLUMN}>
          <Text
            variant="body2Medium"
            style={{
              color: subTitleTextColor,
            }}
            tx="Topic.locked"
          />
        </View>
      )
    } else if (completed) {
      return (
        <View style={COLUMN}>
          <Text
            variant="body2Medium"
            style={{
              color: subTitleTextColor,
            }}
            tx={certEnabled && certLevel ? 'Topic.certified' : 'Common.complete'}
          />
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text
              variant="subtitle"
              style={{ color: subTitleTextColor }}
              text={`${Math.round((topic?.knowledge_pct ?? 0) * 100)}% `}
            />
            <Text variant="subtitle" tx="Profile.recall" style={{ color: subTitleTextColor }} />
          </View>
        </View>
      )
    } else if (!started) {
      return (
        <View style={{ ...COLUMN, justifyContent: 'flex-end' }}>
          <Text
            variant="body2Medium"
            style={{
              color: subTitleTextColor,
            }}
            tx="Topic.notStarted"
          />
          {certEnabled && (
            <Text
              variant="subtitle"
              style={{ color: subTitleTextColor }}
              txOptions={{ level: certLevel }}
              tx={certLevel ? 'Certifications.complete_cert' : 'Certifications.available'}
            />
          )}
        </View>
      )
    } else if (started) {
      return (
        <View style={COLUMN}>
          <Text
            variant="body2Medium"
            style={{
              color: subTitleTextColor,
            }}
            tx="Quiz.inProgress"
          />
          <Text
            variant="subtitle"
            style={{ color: subTitleTextColor }}
            tx="Topic.pctCompleted"
            txOptions={{
              value: Math.round(Number(progressValue) * 100).toFixed(0),
            }}
          />
        </View>
      )
    }
  }

  const renderIcon = () => {
    if (locked) {
      return (
        <View style={{ alignSelf: 'flex-end', height: 28, width: 28 }}>
          <SvgIcon src={lockIcon} />
        </View>
      )
    } else if (certEnabled) {
      let certIcon
      switch (certLevel) {
        case null:
          certIcon = iconCertNotEarned
          break
        case 'Gold':
          certIcon = iconGoldMedallion
          break
        case 'Silver':
          certIcon = iconSilverMedallion
          break
        case 'Bronze':
          certIcon = iconBronzeMedallion
          break
      }

      if (completed && !certLevel) {
        return (
          <RecallWheel
            style={{ alignSelf: 'flex-end' }}
            variant="small"
            icon="check"
            value={topic?.knowledge_pct}
            circleColour={recallWheelComp_circleColour}
            iconColour={recallWheelComp_iconColour}
            strokeColor={recallWheelComp_strokeColour}
          />
        )
      } else if (completed) {
        return (
          <View
            style={{
              top: Platform.OS === 'web' ? 4 : 4.5,
              alignSelf: 'flex-end',
              height: 28,
              width: 28,
            }}
          >
            <RecallWheel
              hideIcon
              style={{
                position: 'absolute',
                zIndex: 9999,
                left: 4,
                top: Platform.OS === 'web' ? 4 : 4.5,
              }}
              value={topic?.knowledge_pct}
              circleColour="#fff"
              strokeColor={color.tint20}
            />
            <SvgIcon src={certIcon} size={28} />
          </View>
        )
      } else if (!started) {
        return <SvgIcon src={iconCertNotStarted} size={28} />
      } else if (started) {
        return (
          <View style={{ alignSelf: 'flex-end', height: 28, width: 28 }}>
            <RecallWheel
              style={{
                position: 'absolute',
                zIndex: 9999,
                left: 4,
                top: Platform.OS === 'web' ? 4 : 4.5,
              }}
              value={progressValue}
              circleColour="#fff"
              strokeColor={color.tint20}
              hideIcon
            />
            <SvgIcon src={certIcon ? certIcon : iconCertNotStarted} size={28} />
          </View>
        )
      }
    } else {
      if (path) {
        return (
          <RecallWheel
            style={{ alignSelf: 'flex-end' }}
            variant="small"
            icon={completed ? 'check' : 'path'}
            iconColour={recallWheel_iconColour}
            value={progressValue}
            circleColour={recallWheel_circleColour}
            strokeColor={recallWheel_strokeColour}
          />
        )
      } else if (locked) {
        return (
          <View style={{ alignSelf: 'flex-end', height: 28, width: 28 }}>
            <SvgIcon src={lockIcon} />
          </View>
        )
      } else if (completed) {
        return (
          <RecallWheel
            style={{ alignSelf: 'flex-end' }}
            variant="small"
            icon="check"
            value={topic?.knowledge_pct}
            circleColour={recallWheelComp_circleColour}
            iconColour={recallWheelComp_iconColour}
            strokeColor={recallWheelComp_strokeColour}
          />
        )
      } else if (!started) {
        return <SvgIcon src={playIcon} size={24} />
      } else if (started) {
        return (
          <RecallWheel
            style={{ alignSelf: 'flex-end' }}
            variant="small"
            icon="play"
            iconColour={recallWheel_iconColour}
            value={progressValue}
            circleColour={recallWheel_circleColour}
            strokeColor={recallWheel_strokeColour}
          />
        )
      }
    }
  }

  return (
    <View
      style={{
        ...CARD,
        marginBottom: Platform.OS === 'web' ? 0 : 12,
        ...style,
        maxWidth: Platform.OS === 'web' ? 386 : Dimensions.get('screen').width / 2 - 26,
      }}
    >
      <TouchableOpacity disabled={locked} style={TOPIC_CARD} onPress={handlePress} {...rest}>
        <View style={CARD_CONTAINER}>
          <View
            style={{
              ...CARD_CONTENT,
              backgroundColor:
                !hideBg && topic?.topic.pack_color ? topic?.topic.pack_color : color.white,
            }}
          >
            <View>
              {Platform.OS === 'web' ? (
                <Image
                  style={{
                    ...IMAGE,
                    aspectRatio: 5 / 2,
                  }}
                  resizeMode="cover"
                  source={imageSource as any}
                />
              ) : (
                <Image
                  resizeMode="cover"
                  style={{ ...IMAGE, flex: 1, aspectRatio: 5 / 2, height: '100%' }}
                  source={imageSource as any}
                />
              )}
              <View style={{ position: 'absolute', top: 6, left: 6 }}>
                {!hideCountdown &&
                  !isSubscriptionWorkspace &&
                  (topic?.end_date || path?.end_datetime) && (
                    <CountdownTimer
                      locale={'en'}
                      style={{
                        height: 22,
                        paddingLeft: 8,
                        paddingRight: 8,
                      }}
                      date={(topic?.end_date || path?.end_datetime) ?? ''}
                    />
                  )}
              </View>
            </View>
            <Text
              numberOfLines={2}
              variant="body1Emphasized"
              text={topic?.topic?.title || path?.name}
              style={{
                ...CARD_TEXT,
                color: !hideBg && !!topic?.topic.pack_color ? color.white : color.black,
              }}
            />
            <View style={FOOTER}>
              <View style={FOOTER_CONTENT}>
                {renderSubtitle()}
                {renderIcon()}
              </View>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    </View>
  )
})

const CARD_CONTAINER: ViewStyle = {
  flex: 1,
  justifyContent: 'center',
  borderRadius: 24,
  backgroundColor: color.white,
}
const CARD: ViewStyle = {
  flex: 1,
  borderRadius: 24,
}
const TOPIC_CARD: ViewStyle = {
  borderRadius: 24,
  flexDirection: 'column',
  justifyContent: 'flex-start',
  borderWidth: 1,
  borderColor: '#EFF0F6',
  display: 'flex',
  flex: 1,
}
const CARD_CONTENT: ViewStyle = {
  padding: 12,
  flex: 1,
  flexDirection: 'column',
  justifyContent: 'space-between',
  borderRadius: 24,
}
const CARD_TEXT: TextStyle = { marginBottom: 16, marginTop: 12 }
const IMAGE: ImageStyle = {
  width: '100%',
  borderRadius: 12,
}
const FOOTER: ViewStyle = {
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  height: 36,
}
const FOOTER_CONTENT: ViewStyle = {
  alignItems: 'center',
  width: '100%',
  flexDirection: 'row',
  justifyContent: 'space-between',
}

const COLUMN: ViewStyle = { flexDirection: 'column' }
